/**
 * Assessment API client (Sprint 6 B4).
 *
 * Mirror backend endpoints:
 *   - GET    /api/assessment/quizzes/?course=<slug>           — list quiz of course
 *   - POST   /api/assessment/attempts/start/                  — start attempt (idempotent)
 *   - GET    /api/assessment/attempts/<id>/                   — detail
 *   - POST   /api/assessment/attempts/<id>/answer/            — partial save
 *   - POST   /api/assessment/attempts/<id>/submit/            — finalize + auto-grade
 */

import type { RubricAssessment, RubricStructure } from "@/admin/api/rubrics";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import type { AiDeclarationInput, StudentAiPolicy } from "@/features/ai/aiPolicy";

// ─── Types ────────────────────────────────────────────────────────────────

export type QuestionType =
  | "multiple_choice"
  | "true_false"
  | "short_answer" // Sprint 30 — AI-assist grading, xem QuizTake.tsx
  | "essay" // Sprint 30 — AI-assist grading, xem QuizTake.tsx
  | "pronunciation"; // plan 2026-10-03 — ghi âm, Gemini chấm sau khi nộp

export type AttemptStatus = "in_progress" | "submitted" | "expired" | "invalidated";

export interface QuizListItem {
  id: number;
  title: string;
  description: string;
  time_limit_minutes: number;
  duration_display: string;
  max_attempts: number;
  pass_threshold_pct: number;
  webcam_required: boolean;
  total_points: number;
  kind?: "assignment" | "exam" | "attendance";
  /** 2026-10-01 — bài tập nên làm sau bài học này. */
  recommended_after_lesson?: { id: number; order: number; title: string; slug: string } | null;
  /** Kỳ học của bài (null = bài tập dùng chung mọi kỳ). */
  term_offering?: number | null;
  term_offering_name?: string | null;
  term_offering_starts_at?: string | null;
  term_offering_ends_at?: string | null;
  /** Khung giờ làm bài (kind=exam) — null = không giới hạn. */
  opens_at?: string | null;
  closes_at?: string | null;
  created_at: string;
}

export type QuizWindowState = "open" | "not_open" | "closed";

/** Trạng thái khung giờ làm bài so với `now` (mặc định thời điểm hiện tại). */
export function quizWindowState(
  quiz: Pick<QuizListItem, "opens_at" | "closes_at">,
  now: Date = new Date(),
): QuizWindowState {
  if (quiz.opens_at && now < new Date(quiz.opens_at)) return "not_open";
  if (quiz.closes_at && now > new Date(quiz.closes_at)) return "closed";
  return "open";
}

/** "20:00 01/10/2026" theo giờ máy người dùng. */
export function formatQuizTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())} ${pad(d.getDate())}/${pad(
    d.getMonth() + 1,
  )}/${d.getFullYear()}`;
}

export interface QuestionChoice {
  id: number;
  text: string;
  order: number;
  /** Chỉ có khi attempt SUBMITTED (review schema). */
  is_correct?: boolean;
}

export interface Question {
  id: number;
  type: QuestionType;
  prompt: string;
  points: number;
  order: number;
  choices: QuestionChoice[];
  /** Câu mẫu cần đọc (pronunciation). */
  speaking_target?: string;
  /** ja|en|zh|ko — ngôn ngữ của môn (pronunciation). */
  speaking_language?: string;
  /** Plan 2026-10-03 — rubric chấm (null: không có / GV không cho xem trước khi nộp). */
  rubric?: RubricStructure | null;
  /** Plan 2026-10-27 — URL audio của đề ("nghe rồi chọn"); "" = không có. */
  audio_url?: string;
}

export interface QuizBrief {
  id: number;
  title: string;
  time_limit_minutes: number;
  duration_display: string;
  pass_threshold_pct: number;
  kind?: "assignment" | "exam" | "attendance";
  webcam_required: boolean;
}

export interface AnswerSnapshot {
  selected_choice_ids: number[];
  /** Câu trả lời text cho short_answer/essay — rỗng cho MC/TF. */
  text_answer: string;
  is_correct: boolean | null;
  points_awarded: number;
  /** Nhận xét cuối instructor chấm (short_answer/essay). Rỗng nếu chưa chấm. */
  feedback: string;
  /** True sau khi instructor chốt điểm (short_answer/essay). */
  manually_graded: boolean;
  /** Plan 2026-10-03 — câu Phát âm. */
  answer_id?: number;
  has_audio?: boolean;
  /** 0–100 Gemini chấm; null = chưa chấm / chấm lỗi (chờ giảng viên). */
  pronunciation_score?: number | null;
  /** Plan 2026-10-03 — bảng điểm theo tiêu chí rubric (bản GV chốt). */
  rubric_result?: RubricAssessment | null;
  /** Plan 2026-10-03 — phúc khảo: đơn hiện có (null nếu chưa), còn nộp được không, hạn. */
  appeal?: GradeAppealInfo | null;
  /** Sếp chốt 2026-10-03 — đã có điểm chính thức chưa / có phải AI chấm (bài tập). */
  is_final?: boolean;
  graded_by_ai?: boolean;
  appeal_open?: boolean;
  appeal_deadline?: string | null;
}

export type AppealStatus = "pending" | "accepted" | "rejected" | "withdrawn";

export interface GradeAppealInfo {
  id: number;
  status: AppealStatus;
  status_label: string;
  reason: string;
  old_points: number;
  new_points: number | null;
  max_points: number;
  response: string;
  created_at: string;
  resolved_at: string | null;
}

export interface AttemptDetail {
  id: number;
  status: AttemptStatus;
  score: number;
  total_points: number;
  passed: boolean;
  /** True nếu còn câu short_answer/essay chưa được instructor chấm — `score`
   * hiện tại chưa tính các câu đó, hiện "Đang chờ chấm" thay vì điểm cuối. */
  needs_manual_grading: boolean;
  started_at: string;
  submitted_at: string | null;
  expired_at: string | null;
  deadline_at: string | null;
  quiz: QuizBrief;
  questions: Question[];
  /** Map question_id (string key vì JSON) → AnswerSnapshot. */
  answers: Record<string, AnswerSnapshot>;
  /** Sprint 6 B5/B6 — proctoring layer. Null nếu attempt cũ trước feature ship. */
  proctor_session_id: number | null;
  consent_given_at: string | null;
  /** TT 83 C5 — chính sách AI hiệu lực; null khi tính năng tắt. Optional cho fixture cũ. */
  ai_policy?: StudentAiPolicy | null;
}

// ─── Hooks ────────────────────────────────────────────────────────────────

/**
 * GET /api/assessment/quizzes/?course=<slug>
 *
 * Chỉ trả published quiz của course đã enroll. Chưa enroll → []
 * (không 403 để FE không lộ "có quiz nhưng cấm xem").
 */
export function useQuizzes(courseSlug: string | undefined) {
  return useQuery({
    queryKey: ["assessment", "quizzes", courseSlug],
    queryFn: async () => {
      if (!courseSlug) return [];
      const resp = await apiClient.get<QuizListItem[]>("/assessment/quizzes/", {
        params: { course: courseSlug },
      });
      return resp.data;
    },
    enabled: !!courseSlug,
    staleTime: 60_000,
  });
}

/**
 * POST /api/assessment/attempts/start/  body: {quiz_id}
 *
 * Idempotent: đã có IN_PROGRESS → 200 return cũ; mới → 201; vượt max_attempts → 409.
 */
export function useStartAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (quizId: number) => {
      const resp = await apiClient.post<AttemptDetail>("/assessment/attempts/start/", {
        quiz_id: quizId,
      });
      return resp.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["assessment", "attempt", data.id] });
    },
  });
}

/**
 * GET /api/assessment/attempts/<id>/
 *
 * IN_PROGRESS → questions không lộ is_correct.
 * SUBMITTED → questions có is_correct cho review screen.
 */
export function useAttempt(attemptId: number | undefined) {
  return useQuery({
    queryKey: ["assessment", "attempt", attemptId],
    queryFn: async () => {
      if (!attemptId) throw new Error("attemptId required");
      const resp = await apiClient.get<AttemptDetail>(`/assessment/attempts/${attemptId}/`);
      return resp.data;
    },
    enabled: !!attemptId,
    staleTime: 5_000,
  });
}

/**
 * POST /api/assessment/attempts/<id>/answer/
 *
 * Partial save mỗi lần user chọn đáp án. Idempotent — update record cũ.
 * KHÔNG invalidate cache ngay (sẽ flash UI) — caller dispatch refetch
 * khi cần (vd: trước submit để đảm bảo save xong).
 */
export function useAnswerQuestion(attemptId: number | undefined) {
  return useMutation({
    mutationFn: async (payload: {
      question_id: number;
      selected_choice_ids?: number[];
      /** Cho short_answer/essay — bỏ qua selected_choice_ids khi dùng field này. */
      text_answer?: string;
    }) => {
      if (!attemptId) throw new Error("attemptId required");
      const resp = await apiClient.post(`/assessment/attempts/${attemptId}/answer/`, payload);
      return resp.data as { detail: string; answer_id: number };
    },
  });
}

/**
 * POST /api/assessment/attempts/<id>/answer/ (multipart) — lưu/ghi đè bản ghi câu
 * Phát âm. Chưa chấm — Gemini chấm sau khi nộp bài (plan 2026-10-03).
 */
export async function uploadPronunciationAudio(
  attemptId: number,
  questionId: number,
  audio: Blob,
): Promise<{ answer_id: number; has_audio: boolean }> {
  const t = audio.type;
  const ext = t.includes("mp4") ? "m4a" : t.includes("ogg") ? "ogg" : "webm";
  const form = new FormData();
  form.append("question_id", String(questionId));
  form.append("audio", audio, `rec.${ext}`);
  const resp = await apiClient.post(`/assessment/attempts/${attemptId}/answer/`, form);
  return resp.data as { answer_id: number; has_audio: boolean };
}

/**
 * Tải bản ghi câu Phát âm (cần JWT → không dùng thẳng URL làm `<audio src>`).
 * `admin=true` → endpoint giảng viên `/api/admin/answers/<id>/audio/`.
 * Trả object URL — caller `URL.revokeObjectURL` khi xong.
 */
export async function fetchAnswerAudioUrl(answerId: number, admin = false): Promise<string> {
  const path = admin
    ? `/admin/answers/${answerId}/audio/`
    : `/assessment/answers/${answerId}/audio/`;
  const resp = await apiClient.get<Blob>(path, { responseType: "blob" });
  return URL.createObjectURL(resp.data);
}

/**
 * POST /api/assessment/attempts/<id>/submit/
 *
 * Finalize → auto-grade MC/TF → return updated AttemptDetail (status=SUBMITTED).
 */
export function useSubmitAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (arg: number | { attemptId: number; aiDeclaration?: AiDeclarationInput }) => {
      const { attemptId, aiDeclaration } = typeof arg === "number" ? { attemptId: arg } : arg;
      const resp = await apiClient.post<AttemptDetail>(
        `/assessment/attempts/${attemptId}/submit/`,
        aiDeclaration ? { ai_declaration: aiDeclaration } : undefined,
      );
      return resp.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["assessment", "attempt", data.id] });
    },
  });
}

// ─── Utility ──────────────────────────────────────────────────────────────

/**
 * Tính phần trăm điểm cho FE display. `total_points=0` → 0%.
 */
export function scorePercent(attempt: Pick<AttemptDetail, "score" | "total_points">): number {
  if (attempt.total_points <= 0) return 0;
  return Math.round((attempt.score / attempt.total_points) * 100);
}

// ─── My term dashboard (dashboard sinh viên theo kỳ học) ───────────────────

export interface MyTermDashboardTerm {
  id: number;
  name: string;
  is_current: boolean;
  /** `ends_at < hôm nay` — kỳ đã kết thúc thì `is_pass` các môn mới có giá trị. */
  is_past: boolean;
}

export interface MyTermDashboardCourse {
  id: number;
  slug: string;
  code: string;
  name: string;
  quiz_count: number;
  /** % (0-100) điểm CỦA CHÍNH BẠN — dùng `pctTo10()` khi hiển thị. `null` =
   *  môn chưa có điểm nào (KHÁC 0 — 0 nghĩa là đã chấm nhưng bị điểm kém). */
  total_pct: number | null;
  /** `null` khi kỳ đang diễn ra (chưa đủ dữ liệu để kết luận đậu/rớt) hoặc
   *  chưa có điểm — chỉ có giá trị boolean ở kỳ ĐÃ QUA + đã có điểm. */
  is_pass: boolean | null;
}

export interface MyTermDashboardResponse {
  term: MyTermDashboardTerm | null;
  /** Toàn bộ kỳ bạn từng enroll (không phải mọi kỳ của trường) — dùng cho dropdown chọn kỳ. */
  terms: MyTermDashboardTerm[];
  courses: MyTermDashboardCourse[];
}

/**
 * GET /api/assessment/me/term-dashboard/?term=<id>
 *
 * `termId` optional — không truyền thì BE tự chọn kỳ hiện tại (nếu bạn có
 * enroll ở đó) hoặc kỳ gần nhất bạn từng enroll.
 */
export function useMyTermDashboard(termId: number | undefined) {
  return useQuery({
    queryKey: ["assessment", "my-term-dashboard", termId],
    queryFn: async () => {
      const resp = await apiClient.get<MyTermDashboardResponse>("/assessment/me/term-dashboard/", {
        params: termId ? { term: termId } : {},
      });
      return resp.data;
    },
  });
}

// ─── Điểm của tôi trong 1 môn, tách theo kỳ (trang môn học, 2026-10-01) ────

export interface MyCourseGradeComponent {
  quiz_id: number;
  quiz_title: string;
  /** % CẤU HÌNH (trọng số) — giữ nguyên %, KHÔNG quy đổi thang 10. */
  weight_pct: number;
  /** % (0-100) điểm đạt được — `pctTo10()` khi hiển thị; `null` = chưa làm. */
  score_pct: number | null;
}

export interface MyCourseGradeTerm {
  term: MyTermDashboardTerm & { starts_at: string | null; ends_at: string | null };
  enrollment_status: "enrolled" | "completed";
  components: MyCourseGradeComponent[];
  /** Điểm thi cuối kỳ — THANG 10 (giảng viên nhập), `null` = chưa có. */
  final_exam: { score: number | null; weight_pct: number };
  total_pct: number | null;
  is_pass: boolean | null;
}

export interface MyCourseGradesResponse {
  course: { id: number; slug: string };
  /** Các kỳ bạn đã đăng ký học môn này — kỳ mới nhất trước. */
  terms: MyCourseGradeTerm[];
}

/** GET /api/assessment/me/course-grades/?course=<slug> */
export function useMyCourseGrades(courseSlug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["assessment", "my-course-grades", courseSlug],
    queryFn: async () => {
      const resp = await apiClient.get<MyCourseGradesResponse>("/assessment/me/course-grades/", {
        params: { course: courseSlug },
      });
      return resp.data;
    },
    enabled: !!courseSlug && enabled,
  });
}
